import axios from 'axios'
import { DEFAULT_NETWORK, BROKER_ID } from '@/constants/config'
import {
  getKeychainString,
  parseKeychainString,
  getOrderlyKey,
  getOrderSignature,
  getOrderlyTradingKey,
  getRequestSignature
} from '@/utils/authentication'

const publicApiPath = [
  '/v1/public/info',
  '/v1/fee/program',
  '/v1/public/broker/name',
  '/v4/public/token'
]
const needOrderSignatureApiPath = [
  'POST/v1/order',
  'PUT/v1/order',
  'DELETE/v1/order',
  'DELETE/v1/orders',
  'DELETE/orderservice/v1/merge/orders/pending',
  'DELETE/orderservice/v1/order'
]

const mainnetApi = axios.create({
  baseURL: 'https://api.orderly.network'
})

const testnetApi = axios.create({
  baseURL: 'https://testnet-api.orderly.org'
})

function getFetcherByNetwork(network) {
  return network === 'testnet' ? testnetApi : mainnetApi
}

function returnResult({ data: { success } }) {
  return success
}

function returnData({ data: { data } }) {
  return data
}

function returnDataRows({ data: { data } }) {
  return data.rows
}

function authenticationInterceptor(config) {
  const keychainString = getKeychainString()
  if (!keychainString || publicApiPath.includes(config.url)) {
    return config
  }

  const { accountId, orderlyKeyPair, orderlyTradingKeyPair } =
    parseKeychainString(keychainString)
  const timestamp = Date.now()
  const httpMethod = config.method.toUpperCase()

  // Prevent Content-Type header been delete if data undefined
  // https://github.com/axios/axios/blob/63e559fa609c40a0a460ae5d5a18c3470ffc6c9e/lib/adapters/xhr.js#L159
  config.data = config.data ? config.data : ''

  if (needOrderSignatureApiPath.includes(`${httpMethod}${config.url}`)) {
    const isDeleteRequest = httpMethod === 'DELETE'
    const orderDataKey = isDeleteRequest ? 'params' : 'data'
    const dataParameters = new URLSearchParams(config[orderDataKey])
    dataParameters.sort()
    config[orderDataKey] = config[orderDataKey] || {}
    config[orderDataKey].signature = getOrderSignature(
      orderlyTradingKeyPair,
      dataParameters.toString()
    )

    config.headers['orderly-trading-key'] = getOrderlyTradingKey(
      orderlyTradingKeyPair
    )
    if (isDeleteRequest) {
      config.headers['content-type'] = 'application/x-www-form-urlencoded'
    }
  }

  const queryString = config.params
    ? `?${new URLSearchParams(config.params).toString()}`
    : ''
  const dataString = config.data ? JSON.stringify(config.data) : config.data

  config.headers['orderly-account-id'] = accountId
  config.headers['orderly-key'] = getOrderlyKey(orderlyKeyPair)
  config.headers['orderly-signature'] = getRequestSignature(orderlyKeyPair, {
    timestamp,
    requestMethod: httpMethod,
    requestPath: config.url,
    queryString,
    dataString
  })
  config.headers['orderly-timestamp'] = timestamp
  return config
}

mainnetApi.interceptors.request.use(authenticationInterceptor, error =>
  Promise.reject(error)
)

testnetApi.interceptors.request.use(authenticationInterceptor, error =>
  Promise.reject(error)
)

class Fetcher {
  constructor(network = DEFAULT_NETWORK) {
    this.network = network
    this.fetcher = getFetcherByNetwork(network)
  }

  setNetwork(network) {
    this.network = network
    this.fetcher = getFetcherByNetwork(network)
  }

  getSystemStatus() {
    return this.fetcher.get('/v1/public/system_info').then(returnData)
  }

  getMarketList() {
    return this.fetcher.get('/v1/public/info').then(returnDataRows)
  }

  getFuturesMarketInfoList() {
    return this.fetcher.get('/v1/public/futures').then(returnDataRows)
  }

  getFuturesMarketInfo(symbol) {
    return this.fetcher.get(`/v1/public/futures/${symbol}`).then(returnData)
  }

  getBrokerList() {
    return this.fetcher.get('/v1/public/broker/name').then(returnDataRows)
  }

  getTokenList() {
    return this.fetcher.get('/v4/public/token').then(returnDataRows)
  }

  getLeverageConfig() {
    return this.fetcher.get('/v1/public/config').then(returnData)
  }

  getAccountHolding() {
    return this.fetcher
      .get('/v1/client/holding', {
        params: {
          all: true
        }
      })
      .then(returnData)
      .then(data => data.holding)
  }

  getAccountBalance() {
    return this.fetcher
      .get('/position/balances')
      .then(returnData)
      .then(data => data.balances)
  }

  updateLeverageSetting(leverage) {
    return this.fetcher.post('/v1/client/leverage', { leverage })
  }

  getOrders(params) {
    return this.fetcher.get('/v1/orders', { params }).then(returnData)
  }

  getLiquidatedPositions(params) {
    const data = params ? { params } : {}
    return this.fetcher.get('/v1/liquidations', data).then(returnData)
  }

  getPositions() {
    return this.fetcher.get('/v1/positions').then(returnData)
  }

  getTradeDetail(orderId) {
    return this.fetcher.get(`/v1/order/${orderId}/trades`).then(returnData)
  }

  // getPendingOrders() {
  //   return this.fetcher
  //     .get('/orderservice/v1/merge/orders/pending', { params: { broker_id: BROKER_ID } })
  //     .then(returnDataRows)
  // }

  getPendingOrders() {
    // return this.getOrders({ status: 'INCOMPLETE', broker_id: BROKER_ID }).then(({ rows }) => rows)
    return this.getOrders({ status: 'INCOMPLETE' }).then(({ rows }) => rows)
  }

  createOrder(order) {
    return this.fetcher.post('/v1/order', order)
  }

  editOrder(order) {
    return this.fetcher.put('/v1/order', order)
  }

  cancelOrder(symbol, orderId) {
    return this.fetcher.delete('/v1/order', {
      params: {
        symbol,
        order_id: orderId
      }
    })
  }

  cancelAllOrder(symbol) {
    if (!symbol) {
      return this.fetcher.delete('/orderservice/v1/merge/orders/pending')
    }
    return this.fetcher.delete('/v1/orders', {
      params: { symbol }
    })
  }

  getNotificationList(params) {
    return this.fetcher
      .get('/v1/notification/inbox/notifications', { params })
      .then(returnDataRows)
  }

  getUnreadNotificationInfo() {
    return this.fetcher.get('/v1/notification/inbox/unread').then(returnData)
  }

  markReadAllNotifications() {
    return this.fetcher.post('/v1/notification/inbox/mark_read_all', {
      flag: 1
    })
  }

  getAssetHistory(params) {
    const data = params ? { params } : {}
    return this.fetcher.get('/v1/asset/history', data).then(returnData)
  }

  getSettlementHistory(params) {
    const data = params ? { params } : {}
    return this.fetcher.get('/v1/pnl_settlement/history', data).then(returnData)
  }

  getrFundingFeeHistory(params) {
    const data = params ? { params } : {}
    return this.fetcher.get('/v1/funding_fee/history', data).then(returnData)
  }

  getFeeProgram() {
    return this.fetcher.get('/v1/fee/program').then(returnDataRows)
  }

  getFuturesFeeProgram() {
    return this.fetcher
      .get('/v1/public/fee_futures/program')
      .then(returnDataRows)
  }

  getAccountInfo() {
    return this.fetcher.get('/v1/client/info').then(returnData)
  }

  updateAccountMaintenanceConfig(params) {
    return this.fetcher
      .post('/v1/client/maintenance_config', params)
      .then(returnResult)
  }

  getAccountTradingVolumeInfo() {
    return this.fetcher.get('/fee/trading_volume/statistics').then(returnData)
  }
}

const fetcher = new Fetcher()

export default fetcher
